import tw from 'twin.macro';
import 'styled-components/macro';

import { Tooltip } from 'component-lib/atoms/tooltip/Tooltip';
import { Button } from 'play/atoms/button/Button';
import { ChallengeStatus, TrackStatus } from 'types/graphql';

import { ReactComponent as CheckIcon } from './images/check.svg';
import { ReactComponent as DotIcon } from './images/dot.svg';

interface ChallengeItemProps {
  title: string;
  slug: string;
  status: ChallengeStatus;
  trackStatus: TrackStatus | null;
  skippingEnabled: boolean;
  onView: () => void;
  onSkip: () => void;
  onContinue: () => void;
  inCurrentChallenge: boolean;
  loading: boolean;
  envBusyTooltip: string;
}

export const ChallengeItem = ({
  title,
  slug,
  status,
  trackStatus,
  skippingEnabled,
  onView,
  onSkip,
  onContinue,
  inCurrentChallenge,
  loading,
  envBusyTooltip,
}: ChallengeItemProps) => (
  <div tw="flex py-1 items-center text-white">
    <div tw="w-5">
      {status === ChallengeStatus.Completed ? (
        <CheckIcon />
      ) : (
        <DotIcon css={status === ChallengeStatus.Locked ? tw`text-gray-30` : null} />
      )}
    </div>

    <div tw="flex-1 pr-4">
      <p tw="line-clamp-2" css={status === ChallengeStatus.Locked ? tw`text-gray-30` : null}>
        {title}
      </p>
    </div>

    <div tw="w-14">
      <ChallengeButtonContainer
        status={status}
        slug={slug}
        trackStatus={trackStatus}
        skippingEnabled={skippingEnabled}
        onView={onView}
        onSkip={onSkip}
        onContinue={onContinue}
        inCurrentChallenge={inCurrentChallenge}
        loading={loading}
        envBusyTooltip={envBusyTooltip}
      />
    </div>
  </div>
);

interface ChallengeButtonContainerProps {
  status: ChallengeStatus;
  slug: string;
  trackStatus: TrackStatus | null;
  skippingEnabled: boolean;
  onView?: () => void;
  onSkip?: () => void;
  onContinue?: () => void;
  inCurrentChallenge: boolean;
  loading: boolean;
  envBusyTooltip: string;
}

const ChallengeButtonContainer = ({
  status,
  slug,
  trackStatus,
  skippingEnabled,
  onView,
  onSkip,
  onContinue,
  inCurrentChallenge,
  loading,
  envBusyTooltip,
}: ChallengeButtonContainerProps) => {
  if (trackStatus && status === ChallengeStatus.Completed && [TrackStatus.Started].includes(trackStatus)) {
    if (inCurrentChallenge) {
      return <p tw="text-base text-center w-full font-medium">Viewing</p>;
    }

    return <ChallengeButton onClick={onView}>View</ChallengeButton>;
  }

  if (
    trackStatus &&
    [TrackStatus.Creating, TrackStatus.Created, TrackStatus.Started].includes(trackStatus) &&
    [
      ChallengeStatus.Unlocked,
      ChallengeStatus.Creating,
      ChallengeStatus.Created,
      ChallengeStatus.Checking,
      ChallengeStatus.Started,
    ].includes(status)
  ) {
    if (inCurrentChallenge) {
      return <p tw="text-base text-center w-full font-medium">Playing</p>;
    }

    return <ChallengeButton onClick={onContinue}>Continue</ChallengeButton>;
  }

  if (skippingEnabled && [TrackStatus.Creating, TrackStatus.Created, TrackStatus.Started].includes(trackStatus!)) {
    return (
      <Tooltip content={envBusyTooltip} disabled={!loading} tw="leading-normal">
        <ChallengeButton onClick={onSkip} disabled={loading}>
          <span data-testid={`skip-to-challenge-${slug}`}>Skip to</span>
        </ChallengeButton>
      </Tooltip>
    );
  }

  return null;
};

const ChallengeButton = tw(Button)`w-full`;
